/**
 * BSGOTECH Global Search — cross-site search bar.
 *
 * Indexes: pages, ecosystem entities, partners, manual volumes, executive PDFs.
 * Fixed top-center pill matching the NeuNexus HomeSearch aesthetic.
 * DE / EN / PT via LangCtx. "/" keyboard shortcut, ESC to close.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Search, X, FileText, BookOpen, Compass, Layers } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";
import { SUPPLIERS } from "@/data/partners";

const P = {
  border: "rgba(255,255,255,0.08)",
  ink:    "#E8ECF0",
  dim:    "rgba(232,236,240,0.68)",
  silver: "#C0C7CE",
  green:  "#22C55E",
  blue:   "#3B82F6",
  rose:   "#F43F5E",
  teal:   "#14B8A6",
  amber:  "#F59E0B",
};

const COPY = {
  en: { placeholder: "Search the BSGOTECH ecosystem…", noResults: "No matches for", groups: { pages: "Pages", ecosystem: "Ecosystem", partners: "Partners", manual: "Corporate Manual", pdf: "Executive PDFs" } },
  de: { placeholder: "Das BSGOTECH-Ökosystem durchsuchen…", noResults: "Keine Treffer für", groups: { pages: "Seiten", ecosystem: "Ökosystem", partners: "Partner", manual: "Corporate Manual", pdf: "Executive-PDFs" } },
  pt: { placeholder: "Pesquisar o ecossistema BSGOTECH…", noResults: "Sem resultados para", groups: { pages: "Páginas", ecosystem: "Ecossistema", partners: "Parceiros", manual: "Manual Corporativo", pdf: "PDFs Executivos" } },
};

const PAGES = [
  { id: "home",           to: "/",                labels: { en: "Home",                    de: "Startseite",                pt: "Início" },                   kws: "bsgotech home landing" },
  { id: "ecosystem",      to: "/our-ecosystem",   labels: { en: "Our Ecosystem",           de: "Unser Ökosystem",           pt: "O nosso ecossistema" },       kws: "ecosystem architecture parent platform divisions" },
  { id: "neunexus",       to: "/neunexus",        labels: { en: "NeuNexus Platform",       de: "NeuNexus Plattform",        pt: "Plataforma NeuNexus" },       kws: "neunexus industrial innovation platform integration" },
  { id: "renalix",        to: "/renalix",         labels: { en: "RENALIX — Health",        de: "RENALIX — Gesundheit",      pt: "RENALIX — Saúde" },           kws: "renalix ai health renal kidney medical clinical fhir mdr" },
  { id: "neugreen",       to: "/neugreen",        labels: { en: "NeuGreen Energy",         de: "NeuGreen Energy",           pt: "NeuGreen Energy" },           kws: "neugreen solar energy renewable battery bess ems" },
  { id: "report",         to: "/report",          labels: { en: "Autonomous Manufacturing Report", de: "Autonomous Manufacturing Report", pt: "Relatório Autonomous Manufacturing" }, kws: "smart factory autonomous manufacturing blueprint uetze industry" },
  { id: "reports",        to: "/reports",         labels: { en: "Executive Reports Hub",   de: "Executive Reports",         pt: "Hub de Relatórios" },         kws: "reports executive pdf blueprint health campus" },
  { id: "manual",         to: "/manual",          labels: { en: "Corporate Manual",        de: "Corporate Manual",          pt: "Manual Corporativo" },        kws: "manual 12 volumes reference founder faq" },
  { id: "leadership",     to: "/leadership",      labels: { en: "Leadership",              de: "Leadership",                pt: "Liderança" },                 kws: "founder ceo cleiton barreto dos santos" },
  { id: "partners",       to: "/partners",        labels: { en: "Partners",                de: "Partner",                   pt: "Parceiros" },                 kws: "partners suppliers ecosystem" },
  { id: "ops",            to: "/ops",             labels: { en: "Operations Center (internal)", de: "Operations Center (intern)", pt: "Operations Center (interno)" }, kws: "ops internal admin documents" },
  { id: "ivory-tours",    to: "/presentations/ivory-tours", labels: { en: "Ivory Tours — Private Presentation", de: "Ivory Tours — Private Präsentation", pt: "Ivory Tours — Apresentação Privada" }, kws: "ivory tours eco collection sao tome german deutsch tourism birdwatching mito obo monte carmo cla ustp pilot private presentation deck" },
];

const ECOSYSTEM = [
  { id: "bsgotech",  to: "/",                 label: "BSGOTECH",         desc: { en: "Parent technology company",         de: "Technologie-Muttergesellschaft",     pt: "Empresa-mãe tecnológica" },         accent: "silver" },
  { id: "neunexus",  to: "/neunexus",         label: "NeuNexus",         desc: { en: "Industrial Innovation Platform",    de: "Industrielle Innovationsplattform",  pt: "Plataforma de Inovação Industrial" }, accent: "green" },
  { id: "renalix",   to: "/renalix",          label: "RENALIX",          desc: { en: "AI-powered healthcare division",    de: "KI-Gesundheitsdivision",             pt: "Divisão de saúde com IA" },          accent: "rose" },
  { id: "neugreen",  to: "/neugreen",         label: "NeuGreen Energy",  desc: { en: "Renewable energy division",         de: "Division für erneuerbare Energie",   pt: "Divisão de energia renovável" },     accent: "teal" },
];

const MANUAL_VOLS = [
  { n: "01", file: "VOL01_Company_Foundation",       label: { en: "Company Foundation",           de: "Unternehmensgrundlage",              pt: "Fundação da Empresa" } },
  { n: "02", file: "VOL02_Business_Architecture",    label: { en: "Business Architecture",        de: "Geschäftsarchitektur",               pt: "Arquitectura de Negócio" } },
  { n: "03", file: "VOL03_Website_Manual",           label: { en: "Website Manual",               de: "Website-Handbuch",                   pt: "Manual do Website" } },
  { n: "04", file: "VOL04_Platform_Documentation",   label: { en: "Platform Documentation",       de: "Plattform-Dokumentation",            pt: "Documentação das Plataformas" } },
  { n: "05", file: "VOL05_Technology",               label: { en: "Technology",                   de: "Technologie",                        pt: "Tecnologia" } },
  { n: "06", file: "VOL06_Business_Processes",       label: { en: "Business Processes",           de: "Geschäftsprozesse",                  pt: "Processos de Negócio" } },
  { n: "07", file: "VOL07_Strategic_Partnerships",   label: { en: "Strategic Partnerships",       de: "Strategische Partnerschaften",       pt: "Parcerias Estratégicas" } },
  { n: "08", file: "VOL08_Sales_Playbook",           label: { en: "Sales Playbook",               de: "Sales Playbook",                     pt: "Manual de Vendas" } },
  { n: "09", file: "VOL09_Founder_Handbook",         label: { en: "Founder Handbook",             de: "Founder-Handbuch",                   pt: "Manual do Fundador" } },
  { n: "10", file: "VOL10_Knowledge_Base_FAQ",       label: { en: "Knowledge Base (FAQ)",         de: "Wissensdatenbank (FAQ)",             pt: "Base de Conhecimento (FAQ)" } },
  { n: "11", file: "VOL11_Website_Content_Library",  label: { en: "Website Content Library",      de: "Website-Inhaltsbibliothek",          pt: "Biblioteca de Conteúdos" } },
  { n: "12", file: "VOL12_AI_Knowledge_Base",        label: { en: "AI Knowledge Base",            de: "KI-Wissensdatenbank",                pt: "Base de Conhecimento para IA" } },
];

const PDFS = [
  { id: "autonomous",  url: "/downloads/The_Autonomous_Manufacturing_Blueprint.pdf",         label: { en: "Autonomous Manufacturing Blueprint (PDF)",     de: "Autonomous Manufacturing Blueprint (PDF)",     pt: "Blueprint Autonomous Manufacturing (PDF)" },     kws: "smart factory blueprint industrial autonomous" },
  { id: "health",      url: "/downloads/NeuNexus_Health_Campus.pdf",                          label: { en: "NeuNexus Health Campus (PDF)",                  de: "NeuNexus Health Campus (PDF)",                  pt: "NeuNexus Health Campus (PDF)" },                  kws: "health campus renalix uetze medical" },
  { id: "research",    url: "/downloads/Predictive_Renal_Healthcare_Blueprint.pdf",           label: { en: "Predictive Renal Healthcare Blueprint (PDF)",   de: "Predictive Renal Healthcare Blueprint (PDF)",   pt: "Predictive Renal Healthcare Blueprint (PDF)" },   kws: "renalix research kidney predictive blueprint" },
  { id: "health-exec", url: "/downloads/NeuNexus_Health_Intelligence_Executive_Concept.pdf",  label: { en: "NeuNexus Health Intelligence — Executive Concept (PDF)", de: "NeuNexus Health Intelligence — Executive Concept (PDF)", pt: "NeuNexus Health Intelligence — Executive Concept (PDF)" }, kws: "renalix executive concept health intelligence proposed" },
  { id: "industry-exec", url: "/downloads/NeuNexus_Industrial_Ecosystem_Executive_Concept.pdf", label: { en: "NeuNexus Industrial Ecosystem — Executive Concept (PDF)", de: "NeuNexus Industrial Ecosystem — Executive Concept (PDF)", pt: "NeuNexus Industrial Ecosystem — Executive Concept (PDF)" }, kws: "industrial neura robotics executive concept physical ai modular energy" },
  { id: "ivory-tours-pdf", url: "/downloads/German_Tourism_Support_Sao_Tome.pdf", label: { en: "German Tourism Support · São Tomé — Ivory Tours Deck (PDF)", de: "German Tourism Support · São Tomé — Ivory Tours Deck (PDF)", pt: "German Tourism Support · São Tomé — Ivory Tours Deck (PDF)" }, kws: "ivory tours eco collection sao tome german deutsch tourism birdwatching cla ustp pilot pdf deck" },
  { id: "italy-market",  url: "/downloads/BSGOTECH_Eco_Collection_Italy_Market_Access_2026.pdf", label: { en: "Eco Collection · Italy Market Access (PDF)", de: "Eco Collection · Italy Market Access (PDF)", pt: "Eco Collection · Acesso ao Mercado Italiano (PDF)" }, kws: "italy italia tour operators eco collection market access pilot suq teresa scacchi" },
  { id: "cv-strategy",   url: "/downloads/BSGOTECH_Cabo_Verde_Integrated_Strategy.pdf", label: { en: "BSGOTECH × Cabo Verde — Integrated Strategy 2026 (PDF)", de: "BSGOTECH × Cabo Verde — Integrated Strategy 2026 (PDF)", pt: "BSGOTECH × Cabo Verde — Integrated Strategy 2026 (PDF)" }, kws: "cabo verde cape institutional strategy nexis nu bai eco collection hx integrated deck" },
];

const ACCENT_MAP = { silver: P.silver, green: P.green, rose: P.rose, teal: P.teal, amber: P.amber, blue: P.blue };

export default function GlobalSearch({ variant = "fixed" }) {
  const { lang } = React.useContext(LangCtx);
  const t = COPY[lang] || COPY.en;
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT","TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault(); inputRef.current?.focus(); setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const results = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return {};
    const match = (str) => (str || "").toLowerCase().includes(term);
    return {
      ecosystem: ECOSYSTEM.filter((e) => match(e.label) || match(e.desc[lang]) || match(e.desc.en) || match(e.id)).slice(0, 4),
      pages: PAGES.filter((p) => match(p.labels[lang]) || match(p.labels.en) || match(p.kws) || match(p.id)).slice(0, 6),
      partners: SUPPLIERS.filter((s) =>
        match(s.name) || match(s.category) || match(s.country) || match(s.description) || match(s.status) || (s.tags || []).some((tag) => match(tag))
      ).slice(0, 6),
      manual: MANUAL_VOLS.filter((v) => match(v.label[lang]) || match(v.label.en) || match(`v${v.n}`) || match(`vol ${v.n}`)).slice(0, 4),
      pdf: PDFS.filter((d) => match(d.label[lang]) || match(d.label.en) || match(d.kws)).slice(0, 4),
    };
  }, [q, lang]);

  const totalMatches = Object.values(results).reduce((a, arr) => a + (arr?.length || 0), 0);
  const wrapCls = variant === "fixed"
    ? "fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[min(560px,calc(100vw-9rem))]"
    : "relative w-full max-w-2xl mx-auto";

  return (
    <div ref={wrapRef} className={wrapCls} data-testid="global-search">
      <div
        className={`flex items-center gap-2 rounded-full transition ${open ? "px-3.5 py-2" : "px-3 py-2"}`}
        style={{ background: "rgba(8,11,18,0.72)", border: `1px solid ${open ? "rgba(34,197,94,0.55)" : P.border}`, backdropFilter: "blur(12px)" }}
      >
        <Search size={13} style={{ color: open ? P.green : P.silver }} className="shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t.placeholder}
          data-testid="global-search-input"
          className="flex-1 bg-transparent outline-none text-[12.5px] placeholder:text-[rgba(228,231,235,0.4)]"
          style={{ color: P.ink }}
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); inputRef.current?.focus(); }}
            data-testid="global-search-clear" aria-label="Clear"
            className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition"
            style={{ color: P.silver }}>
            <X size={11} />
          </button>
        )}
        {!q && (
          <kbd className="hidden sm:inline-flex items-center justify-center text-[9.5px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: "rgba(255,255,255,0.05)", color: P.silver, border: `1px solid ${P.border}` }} aria-hidden="true">/</kbd>
        )}
      </div>

      {open && q && (
        <div className="mt-2 rounded-lg overflow-hidden shadow-2xl"
          style={{ background: "rgba(10,15,26,0.96)", border: `1px solid ${P.border}`, backdropFilter: "blur(16px)" }}
          data-testid="global-search-results">
          {totalMatches === 0 ? (
            <div className="px-4 py-6 text-center">
              <div className="text-[11.5px]" style={{ color: P.dim }}>{t.noResults} <span style={{ color: "#fff" }}>&ldquo;{q}&rdquo;</span></div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto py-1">

              {results.ecosystem?.length > 0 && (
                <div data-testid="gs-group-ecosystem">
                  <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.green }}>
                    <Compass size={10} className="inline mr-1.5 -mt-0.5" />{t.groups.ecosystem}
                  </div>
                  {results.ecosystem.map((e) => (
                    <Link key={e.id} to={e.to} onClick={() => { setOpen(false); setQ(""); }}
                      data-testid={`gs-eco-${e.id}`}
                      className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-white/[0.04]">
                      <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 text-[11px] font-bold"
                        style={{ background: `${ACCENT_MAP[e.accent]}22`, color: ACCENT_MAP[e.accent] }}>{e.label[0]}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{e.label}</div>
                        <div className="text-[11px] truncate" style={{ color: P.dim }}>{e.desc[lang] || e.desc.en}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {results.pages?.length > 0 && (
                <div data-testid="gs-group-pages">
                  <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.blue }}>
                    <Layers size={10} className="inline mr-1.5 -mt-0.5" />{t.groups.pages}
                  </div>
                  {results.pages.map((p) => (
                    <Link key={p.id} to={p.to} onClick={() => { setOpen(false); setQ(""); }}
                      data-testid={`gs-page-${p.id}`}
                      className="flex items-center gap-3 px-4 py-2 transition hover:bg-white/[0.04]">
                      <div className="text-[13px] flex-1 truncate" style={{ color: P.ink }}>{p.labels[lang] || p.labels.en}</div>
                      <div className="text-[10px] font-mono" style={{ color: P.silver }}>{p.to}</div>
                    </Link>
                  ))}
                </div>
              )}

              {results.partners?.length > 0 && (
                <div data-testid="gs-group-partners">
                  <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.teal }}>
                    {t.groups.partners}
                  </div>
                  {results.partners.map((s) => (
                    <Link key={s.id} to={`/partners/${s.id}`} onClick={() => { setOpen(false); setQ(""); }}
                      data-testid={`gs-partner-${s.id}`}
                      className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-white/[0.04]">
                      {s.logo ? (
                        <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.94)" }}>
                          <img src={s.logo} alt="" className="max-h-6 max-w-6 object-contain" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded flex items-center justify-center shrink-0 text-[11px] font-bold"
                          style={{ background: `${P.blue}22`, color: P.blue }}>{s.name[0]}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{s.name}</div>
                        <div className="text-[11px] truncate" style={{ color: P.dim }}>{s.category} · {s.country}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {results.manual?.length > 0 && (
                <div data-testid="gs-group-manual">
                  <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.amber }}>
                    <BookOpen size={10} className="inline mr-1.5 -mt-0.5" />{t.groups.manual}
                  </div>
                  {results.manual.map((v) => {
                    const suffix = lang === "en" ? "" : `.${lang}`;
                    return (
                      <a key={v.n} href={`/downloads/manual/${v.file}${suffix}.md`} target="_blank" rel="noopener noreferrer"
                        onClick={() => { setOpen(false); setQ(""); }}
                        data-testid={`gs-manual-${v.n}`}
                        className="flex items-center gap-3 px-4 py-2 transition hover:bg-white/[0.04]">
                        <div className="text-[10px] font-mono w-8 shrink-0" style={{ color: P.amber }}>V{v.n}</div>
                        <div className="text-[13px] flex-1 truncate" style={{ color: P.ink }}>{v.label[lang] || v.label.en}</div>
                      </a>
                    );
                  })}
                </div>
              )}

              {results.pdf?.length > 0 && (
                <div data-testid="gs-group-pdf">
                  <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.rose }}>
                    <FileText size={10} className="inline mr-1.5 -mt-0.5" />{t.groups.pdf}
                  </div>
                  {results.pdf.map((d) => (
                    <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer"
                      onClick={() => { setOpen(false); setQ(""); }}
                      data-testid={`gs-pdf-${d.id}`}
                      className="flex items-center gap-3 px-4 py-2 transition hover:bg-white/[0.04]">
                      <FileText size={12} style={{ color: P.rose }} className="shrink-0" />
                      <div className="text-[12.5px] flex-1 truncate" style={{ color: P.ink }}>{d.label[lang] || d.label.en}</div>
                    </a>
                  ))}
                </div>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}
